// import React, { useEffect, useState } from "react";
// import { Line } from "react-chartjs-2";
// import {
//   Chart as ChartJS,
//   CategoryScale,
//   LinearScale,
//   PointElement,
//   LineElement,
//   Tooltip,
//   Legend
// } from "chart.js";

// // Register the required components
// ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

// function Infograph({ data, weightOnly }) {
//   const [renderdata, setRenderData] = useState({
//     labels: [],
//     datasets: []
//   });

//   const rawdata1 = {
//     fill: false,
//     lineTension: 0.3,
//     backgroundColor: "#2a2c30",
//     borderColor: "#2a2c30",
//     pointRadius: 2,
//     pointHitRadius: 5,
//   };

//   const rawdata2 = {
//     fill: false,
//     lineTension: 0.3,
//     backgroundColor: "#f84c1e",
//     borderColor: "#f84c1e",
//     pointRadius: 2,
//     pointHitRadius: 5,
//   };

//   const rawdata3 = {
//     fill: false,
//     lineTension: 0.3,
//     backgroundColor: "#1e90ff",
//     borderColor: "#1e90ff",
//     pointRadius: 2,
//     pointHitRadius: 5,
//   };

//   const weightDataStyle = {
//     fill: false,
//     lineTension: 0.3,
//     backgroundColor: "#f84c1e",
//     borderColor: "#f84c1e",
//     pointRadius: 2,
//     pointHitRadius: 5,
//   };

//   useEffect(() => {
//     if (data && data.length > 0) {
//       const updatedData = {
//         labels: data.map(entry => new Date(entry.datetime).toLocaleDateString()),
//         datasets: [],
//       };

//       if (!weightOnly) {
//         updatedData.datasets.push({
//           ...rawdata2,
//           data: data.map(entry => parseInt(entry.systolic)),
//           label: "SBP",
//         });
//         updatedData.datasets.push({
//           ...rawdata1,
//           data: data.map(entry => parseInt(entry.diastolic)),
//           label: "DBP",
//         });
//         updatedData.datasets.push({
//           ...rawdata3,
//           data: data.map(entry => parseInt(entry.pulse)),
//           label: "PR",
//         });
//       }

//       if (weightOnly || data.some(entry => entry.weight)) {
//         updatedData.datasets.push({
//           ...weightDataStyle,
//           data: data.map(entry => parseInt(entry.weight)),
//           label: "Weight (lbs)",
//         });
//       }

//       setRenderData(updatedData);
//     }
//   }, [data, weightOnly]);

//   const options = {
//     responsive: true,
//     plugins: {
//       tooltip: {
//         enabled: true,
//         mode: "nearest",
//         intersect: false,
//         callbacks: {
//           label: function(tooltipItem) {
//             return `${tooltipItem.dataset.label}: ${tooltipItem.raw}`;
//           }
//         },
//       },
//       legend: {
//         display: true,
//         position: "bottom",
//         labels: {
//           usePointStyle: true,
//           font: { size: 8 }
//         }
//       },
//     },
//     scales: {
//       y: {
//         ticks: {
//           font: { size: 8 },
//           callback: function(value) {
//             return value;
//           }
//         },
//         grid: { drawTicks: false },
//       },
//       x: {
//         ticks: {
//           font: { size: 8 },
//           callback: function(value, index) {
//             const label = renderdata.labels[index];
//             const date = new Date(label);
//             return date.toLocaleString("default", { month: "short", day: "numeric" });
//           }
//         },
//         grid: { display: false },
//       },
//     },
//     elements: {
//       point: {
//         radius: 2,
//       },
//     },
//   };

//   return (
//     <div style={{ height: "110px", width: "240px" }}>
//       <Line options={options} data={renderdata} height={150} width={300} />
//     </div>
//   );
// }

// export default Infograph;

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import dayjs from "dayjs";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
);

function Infograph({ data = [], weightOnly = false }) {
  const canvasParentRef = useRef(null);

  // dataset style helper
  const mkStyle = (color) => ({
    fill: false,
    tension: 0.3,
    borderColor: color,
    backgroundColor: color,
    pointStyle: "circle",
    pointRadius: 4,
    pointHoverRadius: 6,
    pointBorderWidth: 2,
    pointBackgroundColor: color,
    pointBorderColor: "#ffffff",
    borderWidth: 2,
    hitRadius: 10,
  });

  const styleSBP = useMemo(() => mkStyle("#f0841d"), []);
  const styleDBP = useMemo(() => mkStyle("#1f2937"), []);
  const stylePR = useMemo(() => mkStyle("#16a34a"), []);
  const styleW = useMemo(() => mkStyle("#f84c1e"), []);

  const [renderdata, setRenderData] = useState({ labels: [], datasets: [] });

  useEffect(() => {
    if (!data?.length) {
      setRenderData({ labels: [], datasets: [] });
      return;
    }

    const labels = data.map((entry) => new Date(entry.datetime).toISOString());
    const updated = { labels, datasets: [] };

    if (!weightOnly) {
      updated.datasets.push({
        ...styleSBP,
        label: "SBP",
        data: data.map((e) => Number(e.systolic)),
      });
      updated.datasets.push({
        ...styleDBP,
        label: "DBP",
        data: data.map((e) => Number(e.diastolic)),
      });
      updated.datasets.push({
        ...stylePR,
        label: "PR",
        data: data.map((e) => Number(e.pulse)),
      });
    }

    if (weightOnly || data.some((e) => e.weight != null)) {
      updated.datasets.push({
        ...styleW,
        label: "Weight (lbs)",
        data: data.map((e) => (e.weight != null ? Number(e.weight) : null)),
      });
    }

    setRenderData(updated);
  }, [data, weightOnly]);

  // External custom tooltip
  // --- replace your externalTooltip with this one (JSX, no TS) ---
  const externalTooltip = (context) => {
    const { chart, tooltip } = context;

    // create (or reuse) a single body-level tooltip element
    let tooltipEl = document.querySelector("#chartjs-ext-tooltip-global");
    if (!tooltipEl) {
      tooltipEl = document.createElement("div");
      tooltipEl.id = "chartjs-ext-tooltip-global";
      Object.assign(tooltipEl.style, {
        position: "absolute",
        pointerEvents: "none",
        zIndex: "9999",
        background: "#fff",
        borderRadius: "12px",
        boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
        padding: "10px 12px",
        minWidth: "140px",
        maxWidth: "240px",
        font: "500 12px Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial",
        opacity: "0",
        transform: "translate(-50%, -100%)",
        transition: "opacity 80ms linear",
      });
      const inner = document.createElement("div");
      inner.className = "inner";
      tooltipEl.appendChild(inner);
      document.body.appendChild(tooltipEl);
    }

    // hide when not active
    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = "0";
      return;
    }

    // content
   if (tooltip.body) {
  const body = tooltip.body[0].lines[0]; // "SBP: 125"
  const [series, val] = body.split(":").map((s) => s.trim());
  const dotColor =
    (tooltip.labelColors && tooltip.labelColors[0]?.backgroundColor) ||
    "#333";
  const titleISO = tooltip.title && tooltip.title[0];

  const dateStr = titleISO ? dayjs(titleISO).format("ddd, MMM D, YYYY") : "";

  tooltipEl.querySelector(".inner").innerHTML = `
    <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;">
      <span style="width:8px;height:8px;border-radius:999px;background:${dotColor};display:inline-block"></span>
      <span style="font-weight:600;color:#111827">${series}</span>
    </div>
    <div style="color:#6b7280;margin-bottom:6px">${dateStr}</div>
    <div style="font-weight:600;color:#111827">Value: ${val}</div>
  `;
}

    // --- positioning (in viewport coords) ---
    const canvasRect = chart.canvas.getBoundingClientRect();
    const caretX = canvasRect.left + window.scrollX + tooltip.caretX;
    const caretY = canvasRect.top + window.scrollY + tooltip.caretY;

    // make it visible to measure size
    tooltipEl.style.opacity = "1";
    tooltipEl.style.transform = "translate(-50%, -100%)";
    const tw = tooltipEl.offsetWidth;
    const th = tooltipEl.offsetHeight;
    const PAD = 10; // space from edges/point

    // clamp inside a bounding box (use the chart's parent card if you want tighter bounds)
    // Here we clamp within the viewport so it never cuts.
    const vw = window.scrollX + document.documentElement.clientWidth;
    const vh = window.scrollY + document.documentElement.clientHeight;

    // X clamp
    let left = caretX;
    const minLeft = window.scrollX + PAD + tw / 2;
    const maxLeft = vw - PAD - tw / 2;
    left = Math.max(minLeft, Math.min(left, maxLeft));

    // Y flip: try above; if not enough room, place below
    let top = caretY - PAD - th; // above
    let transform = "translate(-50%, 0)"; // we’ll adjust after we finalize top
    if (top < window.scrollY + PAD) {
      // below
      top = caretY + PAD;
      transform = "translate(-50%, 0)";
    } else {
      // above
      transform = "translate(-50%, 0)"; // already subtracted th; visually sits above the point
    }

    // final Y clamp (so it never goes past bottom edge)
    const maxTop = vh - PAD - th;
    top = Math.min(Math.max(top, window.scrollY + PAD), maxTop);

    tooltipEl.style.left = `${left}px`;
    tooltipEl.style.top = `${top}px`;
    tooltipEl.style.transform = transform;
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "nearest", intersect: false },
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: { usePointStyle: true, boxWidth: 6, font: { size: 10 } },
      },
      tooltip: {
        enabled: false,
        external: externalTooltip,
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.raw}`,
          title: (items) => items[0]?.label ?? "",
        },
      },
    },
    elements: {
      point: { radius: 4, hoverRadius: 6, borderWidth: 2 },
      line: { borderWidth: 2 },
    },
    scales: {
      x: {
        grid: {
          display: true,
          drawTicks: false,
          color: "#E5E7EB",
          borderDash: [4, 6],
        },
        ticks: {
          maxRotation: 0,
          font: { size: 10 },
          callback: function (_, index) {
            const iso = renderdata.labels[index];
            const d = iso ? new Date(iso) : null;
            return d ? d.toLocaleString("default", { month: "long" }) : "";
          },
        },
      },
      y: {
        grid: {
          drawTicks: false,
          color: "#E5E7EB",
          borderDash: [4, 6],
        },
        ticks: { font: { size: 10 } },
      },
    },
  };

  return (
    <div
      ref={canvasParentRef}
      style={{
        height: 260,
        width: 360,
        position: "relative",
        overflow: "visible",
      }}
    >
      <Line data={renderdata} options={options} />
    </div>
  );
}

export default Infograph;
